<script setup lang="ts">
  import { Plus, Trash2 } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import type { AdTemplateVariable, AdTemplateVariables } from "@/types";

  const variables = defineModel<AdTemplateVariables>({ required: true });

  const update = (entries: Array<[string, AdTemplateVariable]>) => {
    variables.value = Object.fromEntries(entries);
  };

  const rename = (oldKey: string, newKey: string) => {
    if (!newKey || newKey === oldKey || newKey in variables.value) return;
    update(Object.entries(variables.value).map(([key, spec]) => [key === oldKey ? newKey : key, spec]));
  };

  const setField = (key: string, field: keyof AdTemplateVariable, value: string) => {
    const spec = { ...variables.value[key] };
    if (field === "max") {
      if (value) spec.max = Number(value);
      else delete spec.max;
    } else {
      spec[field] = value;
    }
    variables.value = { ...variables.value, [key]: spec };
  };

  const remove = (key: string) => update(Object.entries(variables.value).filter(([other]) => other !== key));

  const add = () => {
    let index = Object.keys(variables.value).length + 1;
    while (`title${index}` in variables.value) index++;
    variables.value = { ...variables.value, [`title${index}`]: { label: "", default: "" } };
  };
</script>

<template>
  <div class="flex flex-col gap-3">
    <p class="text-xs text-muted-foreground">
      Each variable is a field of the creative form. Bind it in the HTML with
      <code class="font-mono">data-text="&lt;name&gt;"</code>
      . Keep the names of an existing template: creatives store their texts under them.
    </p>
    <div class="grid grid-cols-[1fr_1fr_2fr_5rem_2rem] items-center gap-2 text-xs text-muted-foreground">
      <span>Name</span>
      <span>Label</span>
      <span>Default text</span>
      <span>Max</span>
      <span />
    </div>
    <div
      v-for="(spec, key) in variables"
      :key="key"
      class="grid grid-cols-[1fr_1fr_2fr_5rem_2rem] items-center gap-2">
      <Input
        :model-value="key"
        class="font-mono text-xs"
        @change="rename(String(key), ($event.target as HTMLInputElement).value.trim())" />
      <Input
        :model-value="spec.label ?? ''"
        class="text-sm"
        placeholder="Title"
        @update:model-value="setField(String(key), 'label', String($event))" />
      <Input
        :model-value="spec.default ?? ''"
        class="text-sm"
        @update:model-value="setField(String(key), 'default', String($event))" />
      <Input
        :model-value="spec.max ?? ''"
        type="number"
        class="text-sm"
        @update:model-value="setField(String(key), 'max', String($event))" />
      <Button
        size="icon"
        variant="ghost"
        @click="remove(String(key))">
        <Trash2 class="size-4" />
      </Button>
    </div>
    <Button
      size="sm"
      variant="outline"
      class="self-start"
      @click="add">
      <Plus class="size-4" />
      Add variable
    </Button>
  </div>
</template>
